import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cn } from "cn";
import type { ComponentProps, ReactNode } from "react";

/**
 * Панель настроек: рамка без заливки, шапка (плитка-иконка, заголовок,
 * описание, действие справа), строки «подпись и подсказка слева, контрол
 * справа» и подвал с кнопками. Ни данных, ни домена: части собирает экран.
 *
 * Корень — `section`; название раздела связывается через `aria-labelledby`
 * на `titleId` шапки.
 */
function Panel({ className, ...props }: ComponentProps<"section">) {
  return (
    <section
      data-slot="panel"
      className={cn("min-w-0 overflow-hidden rounded-lg border border-border", className)}
      {...props}
    />
  );
}

interface PanelHeaderProps extends Omit<ComponentProps<"div">, "title"> {
  /** Плитка-иконка слева; декоративна. */
  icon?: ReactNode;
  title: ReactNode;
  /** Уровень заголовка в outline страницы; по умолчанию 2. */
  titleLevel?: 2 | 3 | 4 | 5 | 6;
  /** `id` заголовка — на него ссылается `aria-labelledby` панели. */
  titleId?: string;
  description?: ReactNode;
  /** Справа в шапке: бейдж состояния. */
  action?: ReactNode;
}

function PanelHeader({
  className,
  icon,
  title,
  titleId,
  titleLevel = 2,
  description,
  action,
  ...props
}: PanelHeaderProps) {
  const Heading = `h${titleLevel}` as const;
  return (
    <div
      data-slot="panel-header"
      className={cn("flex items-start gap-3 px-5 py-4", className)}
      {...props}
    >
      {icon}
      <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
        <Heading id={titleId} className="text-[15px] leading-[1.2] font-semibold">
          {title}
        </Heading>
        {description ? (
          <p className="text-xs leading-[1.45] text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * Строка настройки. Две ячейки: подпись с подсказкой (`PanelRowText`) и
 * контрол (любой другой ребёнок); на узкой ширине ячейки встают друг под
 * друга. Тег по умолчанию `div`, `render` позволяет сделать строку корнем
 * поля формы.
 */
function PanelRow({ className, render, ...props }: useRender.ComponentProps<"div">) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(
      {
        className: cn(
          "grid grid-cols-1 gap-x-8 gap-y-2 border-t border-border px-5 py-4 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]",
          className,
        ),
      },
      props,
    ),
    render,
    state: { slot: "panel-row" },
  });
}

/** Левая ячейка строки: подпись и подсказка друг под другом. */
function PanelRowText({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="panel-row-text"
      className={cn("flex min-w-0 flex-col gap-1.5", className)}
      {...props}
    />
  );
}

/** Блок на всю ширину панели под разделителем: таблицы и прочее, что не делится на две ячейки. */
function PanelBlock({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="panel-block"
      className={cn("flex min-w-0 flex-col gap-3 border-t border-border px-5 py-4", className)}
      {...props}
    />
  );
}

/** Подвал: слева статус или опасное действие, справа кнопки. */
function PanelFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="panel-footer"
      className={cn(
        "flex min-h-13 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-border bg-muted/30 py-2.5 pr-3 pl-5",
        className,
      )}
      {...props}
    />
  );
}

export type { PanelHeaderProps };
export { Panel, PanelBlock, PanelFooter, PanelHeader, PanelRow, PanelRowText };
